import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Link,
  MenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

interface Organization {
  id: string;
  name: string;
}

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoadingOrgs(true);
        const response = await api.get('/auth/organizations');
        const orgs = Array.isArray(response.data) ? response.data : [];
        setOrganizations(orgs);
        if (orgs.length > 0) {
          setOrganizationId(orgs[0].id);
        }
      } catch (err) {
        console.error('Failed to load organizations:', err);
        setOrganizations([]);
        setError('Failed to load organizations. Please try again later.');
      } finally {
        setLoadingOrgs(false);
      }
    };

    fetchOrganizations();
  }, []);

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = t('auth.invalidEmail') || 'Please enter a valid email address';
    }

    if (!password || password.length < 6) {
      errors.password = t('auth.passwordMinLength') || 'Password must be at least 6 characters';
    }

    if (password !== confirmPassword) {
      errors.confirmPassword = t('auth.passwordMismatch') || 'Passwords do not match';
    }

    if (!firstName || firstName.trim().length === 0) {
      errors.firstName = t('auth.firstNameRequired') || 'First name is required';
    }

    if (!lastName || lastName.trim().length === 0) {
      errors.lastName = t('auth.lastNameRequired') || 'Last name is required';
    }

    if (!organizationId) {
      errors.organizationId = t('auth.organizationRequired') || 'Please select an organization';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/auth/register', {
        email,
        password,
        firstName,
        lastName,
        organizationId,
        preferredLanguage: 'en',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      // Redirect to login page with success message
      navigate('/login', { state: { message: t('auth.registrationSuccess') || 'Registration successful! Please log in.' } });
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.title || t('auth.registrationFailed') || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingOrgs) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading organizations...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography component="h1" variant="h4" color="primary">
              TaskFlow
            </Typography>
            <Typography variant="h6" sx={{ mt: 2 }}>
              {t('auth.registerTitle') || 'Create Account'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('auth.registerSubtitle') || 'Join your team and start managing tasks'}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="firstName"
              label={t('auth.firstName') || 'First Name'}
              name="firstName"
              autoComplete="given-name"
              autoFocus
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={loading}
              error={!!validationErrors.firstName}
              helperText={validationErrors.firstName}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="lastName"
              label={t('auth.lastName') || 'Last Name'}
              name="lastName"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={loading}
              error={!!validationErrors.lastName}
              helperText={validationErrors.lastName}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('auth.emailPlaceholder') || 'Email Address'}
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              error={!!validationErrors.email}
              helperText={validationErrors.email}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('auth.passwordPlaceholder') || 'Password'}
              type="password"
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              error={!!validationErrors.password}
              helperText={validationErrors.password}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label={t('auth.confirmPassword') || 'Confirm Password'}
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              error={!!validationErrors.confirmPassword}
              helperText={validationErrors.confirmPassword}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              select
              id="organizationId"
              label={t('auth.organization') || 'Organization'}
              name="organizationId"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              disabled={loading || organizations.length === 0}
              error={!!validationErrors.organizationId}
              helperText={validationErrors.organizationId || (organizations.length === 0 ? 'No organizations available' : '')}
            >
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </TextField>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || organizations.length === 0}
            >
              {loading ? <CircularProgress size={24} /> : t('auth.registerButton') || 'Register'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link href="#" variant="body2" onClick={() => navigate('/login')}>
                {t('auth.haveAccount') || 'Already have an account?'} {t('auth.loginHere') || 'Log in here'}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default RegisterPage;
